import { z } from "zod";
import {
  API_LIMITS,
  contentVersionSchema,
  courseCodeSchema,
  csrfTokenSchema,
  inviteCodeSchema,
  itemIdSchema,
  majorSchema,
  nameSchema,
  recoveryCodeSchema,
  sceneIdSchema,
  studentIdSchema,
  taskIdSchema,
  timestampSchema,
  usernameSchema,
  uuidSchema,
} from "./primitives";

export const userRoleSchema = z.enum(["teacher", "learner", "admin"]);

export const publicUserSchema = z
  .object({
    id: uuidSchema,
    role: userRoleSchema,
    displayName: z.string().trim().min(1).max(API_LIMITS.name),
  })
  .strict();

export const teacherLoginRequestSchema = z
  .object({
    username: usernameSchema,
    password: z.string().min(8).max(API_LIMITS.password),
  })
  .strict();

export const teacherLoginResponseSchema = z
  .object({
    user: publicUserSchema.extend({ role: z.literal("teacher") }).strict(),
    csrfToken: csrfTokenSchema,
    expiresAt: timestampSchema,
  })
  .strict();

export const learnerJoinRequestSchema = z
  .object({
    inviteCode: inviteCodeSchema,
    name: nameSchema,
    major: majorSchema,
    studentId: studentIdSchema,
  })
  .strict();

export const learnerRecoveryCredentialSchema = z
  .object({
    code: recoveryCodeSchema,
    expiresAt: timestampSchema,
  })
  .strict();

export const learnerProgressTaskSchema = z
  .object({
    taskId: taskIdSchema,
    attemptCount: z.number().int().min(1).max(10_000),
    passed: z.boolean(),
  })
  .strict();

export const learnerProgressSceneSchema = z
  .object({
    sceneId: sceneIdSchema,
    completedNodeIds: z.array(itemIdSchema).max(100),
    tasks: z.array(learnerProgressTaskSchema).max(API_LIMITS.formalTasks),
    completed: z.boolean(),
  })
  .strict()
  .superRefine((scene, context) => {
    if (new Set(scene.completedNodeIds).size !== scene.completedNodeIds.length) {
      context.addIssue({
        code: "custom",
        path: ["completedNodeIds"],
        message: "Completed node IDs must be unique within a scene.",
      });
    }
    const taskIds = scene.tasks.map((task) => task.taskId);
    if (new Set(taskIds).size !== taskIds.length) {
      context.addIssue({
        code: "custom",
        path: ["tasks"],
        message: "Task progress entries must be unique within a scene.",
      });
    }
  });

export const learnerProgressSnapshotSchema = z
  .object({
    authority: z.literal("server"),
    contentVersion: contentVersionSchema,
    generatedAt: timestampSchema,
    latestSceneId: sceneIdSchema.optional(),
    openedMomentIds: z.array(itemIdSchema).max(100),
    completedMomentIds: z.array(itemIdSchema).max(100),
    scenes: z.array(learnerProgressSceneSchema).max(18),
  })
  .strict()
  .superRefine((snapshot, context) => {
    for (const [path, values] of [
      ["openedMomentIds", snapshot.openedMomentIds],
      ["completedMomentIds", snapshot.completedMomentIds],
    ] as const) {
      if (new Set(values).size !== values.length) {
        context.addIssue({
          code: "custom",
          path: [path],
          message: "Progress identifiers must be unique.",
        });
      }
    }
    const sceneIds = snapshot.scenes.map((scene) => scene.sceneId);
    if (new Set(sceneIds).size !== sceneIds.length) {
      context.addIssue({
        code: "custom",
        path: ["scenes"],
        message: "Scene progress entries must be unique.",
      });
    }
  });

export const learnerJoinResponseSchema = z
  .object({
    user: publicUserSchema.extend({ role: z.literal("learner") }).strict(),
    profile: z
      .object({
        name: nameSchema,
        major: majorSchema,
        studentId: studentIdSchema,
      })
      .strict(),
    enrollment: z
      .object({
        id: uuidSchema,
        classroomId: uuidSchema,
        classroomName: z.string().trim().min(1).max(API_LIMITS.className),
        courseCode: courseCodeSchema,
      })
      .strict(),
    csrfToken: csrfTokenSchema,
    expiresAt: timestampSchema,
    recovery: learnerRecoveryCredentialSchema,
    progress: learnerProgressSnapshotSchema,
  })
  .strict();

export const learnerRecoverRequestSchema = z.object({ recoveryCode: recoveryCodeSchema }).strict();

export const learnerRecoverResponseSchema = learnerJoinResponseSchema;

export const learnerRecoveryRotateResponseSchema = z
  .object({
    recovery: learnerRecoveryCredentialSchema,
    progress: learnerProgressSnapshotSchema,
  })
  .strict();

export const logoutResponseSchema = z.object({ ok: z.literal(true) }).strict();
export const csrfResponseSchema = z.object({ csrfToken: csrfTokenSchema }).strict();

export type UserRole = z.infer<typeof userRoleSchema>;
export type PublicUser = z.infer<typeof publicUserSchema>;
export type TeacherLoginRequest = z.infer<typeof teacherLoginRequestSchema>;
export type TeacherLoginResponse = z.infer<typeof teacherLoginResponseSchema>;
export type LearnerJoinRequest = z.infer<typeof learnerJoinRequestSchema>;
export type LearnerJoinResponse = z.infer<typeof learnerJoinResponseSchema>;
export type LearnerRecoverRequest = z.infer<typeof learnerRecoverRequestSchema>;
export type LearnerRecoverResponse = z.infer<typeof learnerRecoverResponseSchema>;
export type LearnerRecoveryCredential = z.infer<typeof learnerRecoveryCredentialSchema>;
export type LearnerRecoveryRotateResponse = z.infer<typeof learnerRecoveryRotateResponseSchema>;
export type LearnerProgressSnapshot = z.infer<typeof learnerProgressSnapshotSchema>;
export type LogoutResponse = z.infer<typeof logoutResponseSchema>;
export type CsrfResponse = z.infer<typeof csrfResponseSchema>;
